import { ArrowBackOutlined } from '@mui/icons-material';
import {
  Breadcrumbs as MUIBreadcrumbs,
  Button,
  Divider,
  Grid,
  Theme,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { capital } from 'case';
import { FC, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '../link';
import { mainNavLinks } from '../../helpers';
import { getOptInFeatureFlagEnabled } from '../../fetch';
import { FeatureFlag, INavLink } from '../../models';
import { SwitchToLegacyModal } from './SwitchToLegacyModal';

interface IBreadcrumbs {
  customPath?: string;
  legacyUrl?: string;
  requiredFeature?: FeatureFlag;
}

enum OverridePaths {
  BuildOptions = 'build-options'
}
export const Breadcrumbs: FC<IBreadcrumbs> = ({ customPath, legacyUrl, requiredFeature }) => {
  const location = useLocation();
  const pathnames = !!customPath
    ? customPath.split('/').filter(x => x)
    : location.pathname.split('/').filter(x => x);
  const hasCrumbs = pathnames.length > 1;
  const classes = useStyles();

  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showOptInButton, setShowOptInButton] = useState(false);

  // look through all nav items for the correct nav item that matches the current route
  // if found and it has a legacyUrl property then show the "Return to original version..." button
  const selectedNavItem: INavLink = useMemo(() => {
    let selectedNavItem: INavLink | null = null;
    const firstLevelNavLinks = mainNavLinks.flatMap(link => link?.links);
    selectedNavItem = firstLevelNavLinks.find(link => link?.url === location.pathname);
    if (!selectedNavItem) {
      selectedNavItem = firstLevelNavLinks
        .flatMap(link => link?.links)
        .find(link => {
          return link?.url === location.pathname;
        });
    }

    return selectedNavItem;
  }, [location]);

  const checkOptIn = async (flag: string) => {
    try {
      const res = await getOptInFeatureFlagEnabled(flag);
      setShowOptInButton(res);
    } catch {
      // don't show any error toast in this context
    }
  };

  useEffect(() => {
    if (requiredFeature || (selectedNavItem?.featureFlag && selectedNavItem?.legacyUrl)) {
      checkOptIn(requiredFeature || selectedNavItem?.featureFlag);
      const timer = setInterval(() => {
        checkOptIn(requiredFeature || selectedNavItem?.featureFlag);
      }, 10000);

      return () => clearInterval(timer);
    }
  }, [requiredFeature, selectedNavItem]);

  const getNavigateTo = (pathnames: string[], index: number) => {
    // overrides for specific routes
    if (pathnames.find(path => path === OverridePaths.BuildOptions)) {
      const routeId = pathnames.find(path => {
        return /^-?\d+$/.test(path);
      });
      return `/sales/contracting/contract-acceptance/build-details/${routeId}?redirect=/sales/contracting/contract-acceptance/${routeId}`;
    }
    const link = `/${pathnames.slice(0, index + 1).join('/')}`;
    return link;
  };

  const renderBreadcrumbs = (
    <MUIBreadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
      {pathnames.map((link, index) => {
        const last = index === pathnames.length - 1;
        const hasNoParentLink =
          link === 'manage' ||
          link === 'orders' ||
          link === 'financial' ||
          link === 'production' ||
          link === 'build' ||
          link === 'documentation' ||
          link === 'reporting' ||
          link === 'items' || // punchlist items /production/punchlists/{id}/items/{itemId}
          link === 'add' || // /development/neighborhoods/add/general-information
          link === 'punchlists' ||
          link === 'manage-content' ||
          link === 'sales-tools' ||
          link === 'vendor-tools' ||
          link === 'checklists' ||
          // style guide categories
          link === 'components' ||
          link === 'inputs' ||
          link === 'grids' ||
          link === 'options' ||
          link === 'plans' ||
          link === 'possessions' ||
          link === 'build-details' ||
          link === 'contracting' ||
          link === 'user-tools' ||
          link === 'option-sets-and-palettes' ||
          link === 'job-titles' ||
          link === 'homeowner-messaging' ||
          link === 'photo-upload';
        const to = getNavigateTo(pathnames, index);
        const isNumeric = /^-?\d+$/.test(link);
        const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(link);

        return last || hasNoParentLink || isNumeric || isGuid ? (
          <Typography variant="body2" key={`${index}`}>
            {capital(link)}
          </Typography>
        ) : (
          <Link key={`${index}`} to={to} className={classes.link}>
            {capital(link)}
          </Link>
        );
      })}
    </MUIBreadcrumbs>
  );

  const renderBackLink = (
    <>
      <Button
        className="print--none"
        variant="text"
        color="primary"
        startIcon={<ArrowBackOutlined />}
        onClick={() => {
          setShowReasonModal(true);
        }}
        sx={{
          position: 'relative',
          top: theme => theme.spacing(-0.5),
          marginBottom: theme => theme.spacing(-0.5)
        }}
      >
        Return to original version of module
      </Button>
      <Divider className={classes.divider} />
      <SwitchToLegacyModal
        requiredFeature={requiredFeature || selectedNavItem?.featureFlag}
        legacyUrl={legacyUrl ?? selectedNavItem?.legacyUrl}
        isOpen={showReasonModal}
        onClose={() => setShowReasonModal(false)}
      />
    </>
  );

  return (
    <>
      {showOptInButton &&
      (legacyUrl || (selectedNavItem?.featureFlag && selectedNavItem?.legacyUrl)) ? (
        <Grid container spacing={{ xs: 0, sm: 2 }}>
          {hasCrumbs && (
            <Grid item xs={12} sm={'auto'} flex={{ xs: '0 0 auto', sm: '1' }}>
              {renderBreadcrumbs}
            </Grid>
          )}
          <Grid item xs={12} sm={'auto'}>
            {renderBackLink}
          </Grid>
        </Grid>
      ) : hasCrumbs ? (
        renderBreadcrumbs
      ) : null}
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  link: {
    fontSize: '0.875rem'
  },
  breadcrumbs: {
    marginBottom: theme.spacing(1)
  },
  divider: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  }
}));
